import {
  Box,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { BsLightningCharge } from 'react-icons/bs';
import { FiChevronDown } from 'react-icons/fi';
import { useAutomation } from '../../../hooks/useAutomation';
import { useListNabeehServiceCategories } from '../../../services/automation.queries';
import type { IMasterDataEventType, IMasterDataEventTiming, ITaskAutomationDetail } from '../../../types';
import type { IAutomationItem } from '../../../types';

export interface IEditStep1FormValues {
  name: string;
  serviceId: string;
  serviceName: string;
  eventTypeId: string;
  eventTimingId: string;
  time: string;
  timeType: string;
}

interface IEditStepOneProps {
  task: IAutomationItem;
  automationDetail?: ITaskAutomationDetail | null;
  isActive: boolean;
  onSubmit: (data: IEditStep1FormValues) => void;
  submitRef: React.MutableRefObject<(() => void) | null>;
}

const EditStepOne = ({ task, automationDetail, isActive, onSubmit, submitRef }: IEditStepOneProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === 'ar';

  // Queries
  const { GetMasterDataQuery } = useAutomation();
  const { data: masterData, isLoading: isMasterDataLoading } = GetMasterDataQuery('automations');
  const { data: serviceCategoriesData, isLoading: isServicesLoading } = useListNabeehServiceCategories();

  const eventTypes: IMasterDataEventType[] = masterData?.result?.eventTypes ?? [];
  const eventTimings: IMasterDataEventTiming[] = masterData?.result?.eventTimings ?? [];
  const serviceCategories = serviceCategoriesData?.result ?? [];

  // Form - pre-filled with task data + automation detail from API
  const {
    control,
    handleSubmit,
    watch,
    resetField,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IEditStep1FormValues>({
    defaultValues: {
      name: automationDetail?.name || task.name || '',
      serviceId: automationDetail?.categoryId || task.serviceId || '',
      serviceName: automationDetail?.categoryName || '',
      eventTypeId: automationDetail?.eventType ? String(automationDetail.eventType) : '',
      eventTimingId: automationDetail?.eventTiming ? String(automationDetail.eventTiming) : '',
      time: automationDetail?.time ? String(automationDetail.time) : '',
      timeType: automationDetail?.timeType ? String(automationDetail.timeType) : '',
    },
  });

  const eventTimingWatch = watch('eventTimingId');
  const eventTypeWatch = watch('eventTypeId');

  // "At" timing (id=3) means no time/timeType needed
  const isAtTiming = eventTimingWatch === '3';

  useEffect(() => {
    if (isAtTiming) {
      resetField('time');
      resetField('timeType');
    }
  }, [isAtTiming, resetField]);

  // One-shot reset when automationDetail arrives (fixes stale-cache display bug)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (automationDetail && !hasInitialized.current) {
      hasInitialized.current = true;
      reset({
        name: automationDetail.name || task.name || '',
        serviceId: automationDetail.categoryId || task.serviceId || '',
        serviceName: automationDetail.categoryName || '',
        eventTypeId: automationDetail.eventType ? String(automationDetail.eventType) : '',
        eventTimingId: automationDetail.eventTiming ? String(automationDetail.eventTiming) : '',
        time: automationDetail.time ? String(automationDetail.time) : '',
        timeType: automationDetail.timeType ? String(automationDetail.timeType) : '',
      });
    }
  }, [automationDetail]);

  // Expose handleSubmit to parent via ref — only when this step is active
  useEffect(() => {
    if (isActive) {
      submitRef.current = handleSubmit(onSubmit);
    }
  }, [isActive, handleSubmit, onSubmit, submitRef]);

  const eventTimingOptions = useMemo(() => {
    return eventTimings.map((item) => ({
      value: item.id.toString(),
      label: isRtl ? item.description : item.descriptionEn,
    }));
  }, [eventTimings, isRtl]);

  const eventTypeOptions = useMemo(() => {
    return eventTypes.map((item) => ({
      value: item.id.toString(),
      label: isRtl ? item.description : item.descriptionEn,
    }));
  }, [eventTypes, isRtl]);

  const timeTypeOptions = useMemo(() => {
    return [
      { label: t('Automation.Hours'), value: '1' },
      { label: t('Automation.Days'), value: '2' },
      { label: t('Automation.Minutes'), value: '3' },
    ];
  }, [t]);

  // Get the selected event type label for the timing chip
  const selectedEventTypeLabel = useMemo(() => {
    const found = eventTypes.find((e) => e.id.toString() === eventTypeWatch);
    if (!found) return '';
    return isRtl ? found.description : found.descriptionEn;
  }, [eventTypeWatch, eventTypes, isRtl]);

  // Shared input sx
  const inputSx = {
    fontFamily: 'Tajawal, sans-serif',
    fontSize: '14px',
    color: '#331860',
    borderRadius: '12px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#EAEEF4',
      borderRadius: '12px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#EAEEF4',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#5B3FFF',
    },
  };

  const labelSx = {
    fontFamily: 'Tajawal, sans-serif',
    fontWeight: 700,
    fontSize: '16px',
    color: '#625783',
    textAlign: isRtl ? 'right' as const : 'left' as const,
    mb: '8px',
  };

  if (isMasterDataLoading || isServicesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8" style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }} />
      </div>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {/* Task Name */}
      <Box>
        <Typography sx={labelSx}>{t('Automation.taskName')}</Typography>
        <Controller
          name="name"
          control={control}
          rules={{ required: t('Automation.taskNameRequired') }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              placeholder={t('Automation.taskNamePlaceholder')}
              error={!!errors.name}
              helperText={errors.name?.message}
              InputProps={{ sx: inputSx }}
              inputProps={{
                style: {
                  fontFamily: 'Tajawal, sans-serif',
                  fontSize: '14px',
                  color: '#331860',
                },
              }}
            />
          )}
        />
      </Box>

      {/* Service Type (Nabeeh) */}
      <Box>
        <Typography sx={labelSx}>{t('Automation.taskType')}</Typography>
        <Controller
          name="serviceId"
          control={control}
          rules={{ required: t('Automation.taskTypeRequired') }}
          render={({ field }) => (
            <Select
              {...field}
              onChange={(e) => {
                field.onChange(e);
                const found = serviceCategories.find((s: any) => s.id === e.target.value);
                setValue('serviceName', found?.name || '');
              }}
              fullWidth
              displayEmpty
              IconComponent={FiChevronDown}
              error={!!errors.serviceId}
              sx={{
                ...inputSx,
                '& .MuiSelect-icon': {
                  color: '#261B6A',
                  ...(isRtl ? { left: '14px', right: 'auto' } : {}),
                },
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography
                      sx={{
                        fontFamily: 'Tajawal, sans-serif',
                        fontSize: '14px',
                        color: '#8E98A1',
                      }}
                    >
                      {t('Automation.selectService')}
                    </Typography>
                  );
                }
                const found = serviceCategories.find((s: any) => s.id === selected);
                return found?.name || selected;
              }}
            >
              {serviceCategories.map((service: any) => (
                <MenuItem
                  key={service.id}
                  value={service.id}
                  sx={{ fontFamily: 'Tajawal, sans-serif' }}
                >
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </Box>

      {/* Event Type */}
      <Box>
        <Typography sx={labelSx}>{t('Automation.event')}</Typography>
        <Controller
          name="eventTypeId"
          control={control}
          rules={{ required: t('Automation.eventRequired') }}
          render={({ field }) => (
            <Select
              {...field}
              fullWidth
              displayEmpty
              IconComponent={FiChevronDown}
              error={!!errors.eventTypeId}
              sx={{
                ...inputSx,
                '& .MuiSelect-icon': {
                  color: '#261B6A',
                  ...(isRtl ? { left: '14px', right: 'auto' } : {}),
                },
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography
                      sx={{
                        fontFamily: 'Tajawal, sans-serif',
                        fontSize: '14px',
                        color: '#8E98A1',
                      }}
                    >
                      {t('Automation.selectEvent')}
                    </Typography>
                  );
                }
                const found = eventTypes.find((e) => e.id.toString() === selected);
                return isRtl ? found?.description : found?.descriptionEn;
              }}
            >
              {eventTypeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ fontFamily: 'Tajawal, sans-serif' }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </Box>

      {/* Timing */}
      <Box>
        <Typography sx={labelSx}>{t('Automation.timing')}</Typography>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              backgroundColor: '#FAFAFA',
              borderRadius: '12px',
              p: '4px 8px',
              height: '42px',
            }}
          >
            <Controller
              name="eventTimingId"
              control={control}
              rules={{ required: t('Automation.timingRequired') }}
              render={({ field }) => (
                <Select
                  {...field}
                  displayEmpty
                  IconComponent={FiChevronDown}
                  sx={{
                    flex: 1,
                    height: '100%',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    fontFamily: 'Tajawal, sans-serif',
                    fontSize: '14px',
                    color: '#261B6A',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#EBECED',
                      borderRadius: '12px',
                    },
                    '& .MuiSelect-icon': {
                      color: '#261B6A',
                      ...(isRtl ? { left: '8px', right: 'auto' } : {}),
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) return t('Automation.selectTiming');
                    const found = eventTimings.find((e) => e.id.toString() === selected);
                    return isRtl ? found?.description : found?.descriptionEn;
                  }}
                >
                  {eventTimingOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{ fontFamily: 'Tajawal, sans-serif' }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            {!isAtTiming && (
              <>
                <Controller
                  name="time"
                  control={control}
                  rules={{
                    required: !isAtTiming ? t('Automation.timeRequired') : false,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      placeholder="0"
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          height: '100%',
                          backgroundColor: '#FFFFFF',
                          borderRadius: '12px',
                          fontFamily: 'Tajawal, sans-serif',
                          fontSize: '14px',
                          color: '#331860',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#EBECED',
                            borderRadius: '12px',
                          },
                        },
                      }}
                      inputProps={{
                        min: 1,
                        style: {
                          fontFamily: 'Tajawal, sans-serif',
                          fontSize: '14px',
                          padding: '8px',
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="timeType"
                  control={control}
                  rules={{
                    required: !isAtTiming ? t('Automation.timeTypeRequired') : false,
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      displayEmpty
                      IconComponent={FiChevronDown}
                      sx={{
                        flex: 1,
                        height: '100%',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        fontFamily: 'Tajawal, sans-serif',
                        fontSize: '14px',
                        color: '#261B6A',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#EBECED',
                          borderRadius: '12px',
                        },
                        '& .MuiSelect-icon': {
                          color: '#261B6A',
                          ...(isRtl ? { left: '8px', right: 'auto' } : {}),
                        },
                      }}
                      renderValue={(selected) => {
                        if (!selected) return t('Automation.selectTimeType');
                        const found = timeTypeOptions.find((o) => o.value === selected);
                        return found?.label || selected;
                      }}
                    >
                      {timeTypeOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          sx={{ fontFamily: 'Tajawal, sans-serif' }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {selectedEventTypeLabel && (
                  <Box
                    sx={{
                      backgroundColor: '#EEEBFF',
                      borderRadius: '12px',
                      px: '16px',
                      py: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <BsLightningCharge size={16} color="#261B6A" />
                    <Typography
                      sx={{
                        fontFamily: 'Tajawal, sans-serif',
                        fontSize: '14px',
                        color: '#261B6A',
                      }}
                    >
                      {selectedEventTypeLabel}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditStepOne;
